import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang } = useLang();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const t = {
    about: lang === 'en' ? 'About' : 'À propos',
    menu: lang === 'en' ? 'Offers' : 'Offres',
    portfolio: 'Portfolio',
    contact: 'Contact',
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/80 backdrop-blur-md py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="cursor-pointer"
            onClick={() => scrollToSection('hero')}
          >
            <h1 className="text-2xl md:text-3xl font-brand-display text-[#d4a574]">
              Cocoonut Digital
            </h1>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className="nav-link text-white hover:text-[#d4a574] transition-colors"
            >
              {t.about}
            </button>
            <Link
              to={`/menu${lang === 'en' ? '?lang=en' : ''}`}
              className="nav-link text-white hover:text-[#d4a574] transition-colors"
            >
              {t.menu}
            </Link>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="nav-link text-white hover:text-[#d4a574] transition-colors"
            >
              {t.portfolio}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="cta-button bg-[#d4a574] text-white hover:bg-[#e8c9a0] transition-all"
            >
              {t.contact}
            </button>

            {/* Language Switcher */}
            <div className="flex items-center text-sm" data-testid="lang-switcher-desktop">
              <button
                onClick={() => setLang('fr')}
                className={`transition-colors ${lang === 'fr' ? 'text-[#d4a574] font-semibold' : 'text-white/50 hover:text-white'}`}
              >
                FR
              </button>
              <span className="text-white/30 mx-1.5">|</span>
              <button
                onClick={() => setLang('en')}
                className={`transition-colors ${lang === 'en' ? 'text-[#d4a574] font-semibold' : 'text-white/50 hover:text-white'}`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-4">
            {/* Mobile Language Switcher */}
            <div className="flex items-center text-xs" data-testid="lang-switcher-mobile">
              <button
                onClick={() => setLang('fr')}
                className={`transition-colors ${lang === 'fr' ? 'text-[#d4a574] font-semibold' : 'text-white/50'}`}
              >
                FR
              </button>
              <span className="text-white/30 mx-1">|</span>
              <button
                onClick={() => setLang('en')}
                className={`transition-colors ${lang === 'en' ? 'text-[#d4a574] font-semibold' : 'text-white/50'}`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white hover:text-[#d4a574] transition-colors"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden mt-6 pb-6 space-y-4"
          >
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-white hover:text-[#d4a574] transition-colors py-2"
            >
              {t.about}
            </button>
            <Link
              to={`/menu${lang === 'en' ? '?lang=en' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left text-white hover:text-[#d4a574] transition-colors py-2"
            >
              {t.menu}
            </Link>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="block w-full text-left text-white hover:text-[#d4a574] transition-colors py-2"
            >
              {t.portfolio}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="cta-button w-full bg-[#d4a574] text-white hover:bg-[#e8c9a0] transition-all"
            >
              {t.contact}
            </button>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
